/**
 * Adaptiver Kontrast für den Header (Produktseite).
 *
 * Auf der Produktseite endet das Produktbild exakt in der Bildschirmmitte – also
 * genau dort, wo das Monogramm im Header steht. Die linke Logohälfte (B) liegt damit
 * über dem Produktfoto, die rechte (S) über der weißen Detailspalte.
 *
 * Dieses Modul misst für jedes Header-Element einzeln, was tatsächlich dahinter liegt,
 * und setzt darauf `data-header-contrast="dark"|"light"`. Die CSS-Regeln dazu stehen in
 * sections/header.liquid und überschreiben nur die Farbvariablen – Menülinks, Burger,
 * Aktions-Icons und das Logo ziehen ihre Farbe damit aus derselben Quelle. Eine
 * gemeinsame Header-Farbe kann diese Seite nicht abdecken: links liegen die Elemente
 * über dem Produktfoto, rechts über der weißen Detailspalte.
 *
 * Das Logo ist eine `currentColor`-Maske und damit ein Farbziel wie jedes andere. Weil
 * die Bildkante mitten hindurch läuft, wird es aus zwei deckungsgleichen Flächen gebaut
 * (Basis + an der Kante beschnittene Kopie), die getrennt gemessen werden – jede Hälfte
 * bekommt so exakt die Farbe der Nav-Items auf ihrer Seite der Kante.
 *
 * Fällt eine Messung aus (kein CORS, kein Canvas, kein Treffer), wird das Attribut
 * entfernt und es bleibt bei der Themefarbe – dem Zustand ohne dieses Skript.
 */

const LOGO_SELECTOR = '.header-logo';
const BASE_SELECTOR = '.header-logo__image--base';
const SEAM_SELECTOR = '.header-logo__image--seam';
const MEDIA_SELECTOR = '.product-information__media';

/**
 * Header-Elemente, die ihre Farbe einzeln nach ihrem eigenen Hintergrund richten.
 * Bewusst nur die sichtbare Kopfzeile: Die Drawer-Links (`.menu-drawer__menu-item`)
 * liegen in einem eigenen Panel mit eigener Fläche und werden nicht mitgemessen.
 * Das Logo fehlt hier bewusst – es wird in updateLogo() halbseitig gemessen.
 */
const TEXT_TARGET_SELECTOR = [
  '.header__row .menu-list__link',
  '.header__row .header__icon--menu',
  '.header__row .header-actions__action',
].join(', ');

/**
 * Wahrgenommene Helligkeit (CIE L*, 0–100) mit Totzone: Erst unter ENTER_DARK wird auf
 * Weiß gewechselt, erst über ENTER_LIGHT zurück auf Schwarz. Ohne diese Hysterese würde
 * das Logo beim Scrollen über Motive nahe der Schwelle hin- und herspringen.
 *
 * Die Grenze liegt bewusst über der Mitte: Die Betonhintergründe der Produktfotos messen
 * um L* 57 und sollen das weiße Logo tragen. Reine Studio-/Weißaufnahmen liegen bei L* 85+
 * und bleiben damit sicher auf Schwarz.
 */
const ENTER_DARK = 68;
const ENTER_LIGHT = 78;
/** Ab diesem Alpha deckt ein Hintergrund das Darunterliegende ab. */
const OPAQUE_ALPHA = 0.9;
/** Kantenlänge des Sample-Rasters, auf das eine Logohälfte heruntergerechnet wird. */
const SAMPLE_SIZE = 8;
/** Breite, auf die Produktbilder zum Messen verkleinert werden. */
const SAMPLE_IMAGE_WIDTH = 160;

/** @type {Map<string, Promise<ImageBitmap|null>>} */
const bitmaps = new Map();

/** @type {CanvasRenderingContext2D|null} */
let context = null;
let frame = 0;
let running = false;
let queued = false;

init();

function init() {
  // Ohne die gemessene Fläche gibt es nichts zu messen; Logo-Overlays sind dagegen
  // optional – die Schriftfarbe wird auch dann gesteuert, wenn das Logo statisch bleibt.
  const media = document.querySelector(MEDIA_SELECTOR);
  if (!media) return;

  // Ab 990px scrollt .page-wrapper statt des Fensters. Scroll-Events steigen nicht auf,
  // werden in der Capture-Phase aber trotzdem zugestellt – damit sind beide Fälle erfasst.
  addEventListener('scroll', schedule, { capture: true, passive: true });
  addEventListener('resize', schedule);

  // Variantenwechsel und Section-Rendering tauschen die Bilder im Gallery-Markup aus.
  new MutationObserver(schedule).observe(media, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['src', 'srcset', 'style', 'class'],
  });

  // Layoutwechsel verschieben das Logo relativ zum Bild.
  new ResizeObserver(schedule).observe(media);

  // Der Sticky-Header fährt nach dem letzten Scroll-Event noch ein und schiebt dabei
  // sowohl das Logo als auch seinen deckenden Underlay in Position. Ohne diese beiden
  // Auslöser bliebe die Messung auf dem Stand von vor der Animation stehen.
  const header = document.querySelector('#header-component');
  if (header) {
    new MutationObserver(schedule).observe(header, {
      attributes: true,
      attributeFilter: ['data-sticky-state', 'data-scroll-direction', 'transparent', 'class', 'style'],
    });
    header.addEventListener('transitionend', schedule);
  }

  schedule();
  // Erneut messen, sobald die Galeriebilder dekodiert sind.
  if (document.readyState !== 'complete') addEventListener('load', schedule, { once: true });
}

function schedule() {
  if (frame) return;
  frame = requestAnimationFrame(() => {
    frame = 0;
    run();
  });
}

async function run() {
  // Messungen sind asynchron (Bild-Fetch); parallele Läufe würden sich überholen.
  if (running) {
    queued = true;
    return;
  }
  running = true;
  try {
    await update();
  } catch {
    // Kein Grund, etwas anzufassen – es bleibt bei der Themefarbe.
  } finally {
    running = false;
    if (queued) {
      queued = false;
      schedule();
    }
  }
}

async function update() {
  await Promise.all([updateLogo(), updateTextTargets()]);
}

/**
 * Die Logofläche liegt auf der Produktseite über zwei verschieden hellen Flächen. Basis
 * und beschnittene Kopie werden darum getrennt gemessen: Die Basis bekommt die Farbe der
 * Fläche links der Bildkante, die Kopie die der Fläche rechts davon. Ohne Kopie (alle
 * Templates außer Produkt) gibt es keine Kante – das Logo nimmt dann unverändert die
 * Farbe aus der Kaskade, genau wie die übrigen Nav-Items.
 */
async function updateLogo() {
  const logo = document.querySelector(LOGO_SELECTOR);
  const base = logo?.querySelector(BASE_SELECTOR);
  const seamHalf = logo?.querySelector(SEAM_SELECTOR);
  if (!logo || !base || !seamHalf) return;

  const rect = base.getBoundingClientRect();
  if (rect.width < 1 || rect.height < 1) return;

  const seam = seamOffset(rect);
  logo.style.setProperty('--header-logo-seam', `${seam.toFixed(2)}px`);

  await Promise.all([
    applyContrast(base, { left: rect.left, top: rect.top, width: seam, height: rect.height }),
    applyContrast(seamHalf, {
      left: rect.left + seam,
      top: rect.top,
      width: rect.width - seam,
      height: rect.height,
    }),
  ]);
}

/**
 * Jedes Textelement wird über seiner eigenen Fläche gemessen. Ein gemeinsamer Wert für
 * die ganze Zeile wäre für die halb über dem Bild liegende Kopfzeile immer für eine
 * Seite falsch.
 */
async function updateTextTargets() {
  const targets = document.querySelectorAll(TEXT_TARGET_SELECTOR);
  await Promise.all(
    Array.from(targets, (element) => applyContrast(element, element.getBoundingClientRect()))
  );
}

/**
 * Abstand der Bildkante vom linken Logorand, in Logo-Koordinaten.
 * 0 = keine Bildspalte hinter dem Logo, volle Breite = Logo liegt komplett auf dem Bild.
 */
function seamOffset(rect) {
  const media = document.querySelector(MEDIA_SELECTOR);
  if (!media) return 0;
  const box = media.getBoundingClientRect();
  // Die Galerie wird per negativem margin unter den Header gezogen; der Container
  // beginnt darum tiefer als das sichtbare Bild. Für die vertikale Prüfung reicht,
  // dass sich beide Boxen überhaupt überschneiden.
  if (box.bottom <= rect.top) return 0;
  return clamp(box.right - rect.left, 0, rect.width);
}

/**
 * Setzt `data-header-contrast` auf ein Element anhand der Helligkeit hinter `rect`.
 * Ohne messbare Fläche (ausgeblendete Menü-Variante, zur Breite 0 geschrumpfte
 * Logohälfte) oder ohne verwertbare Messung wird das Attribut entfernt, damit die
 * Farbe wieder aus der Kaskade kommt.
 */
async function applyContrast(element, rect) {
  if (rect.width < 1 || rect.height < 1) {
    delete element.dataset.headerContrast;
    return;
  }

  const lightness = await areaLightness(rect);
  if (lightness == null) {
    delete element.dataset.headerContrast;
    return;
  }

  const wasDark = element.dataset.headerContrast === 'dark';
  element.dataset.headerContrast = lightness < (wasDark ? ENTER_LIGHT : ENTER_DARK) ? 'dark' : 'light';
}

async function areaLightness(rect) {
  const x = rect.left + rect.width / 2;
  const probe =
    backdropAt(x, rect.top + rect.height / 2) ??
    backdropAt(x, rect.top + rect.height * 0.2) ??
    backdropAt(x, rect.top + rect.height * 0.8);

  if (!probe) return null;
  if (probe.color) return lstar(relativeLuminance(probe.color.r, probe.color.g, probe.color.b));

  const bitmap = await bitmapFor(probe.image);
  if (!bitmap) return null;
  const luminance = imageLuminance(bitmap, probe.image, rect);
  return luminance == null ? null : lstar(luminance);
}

/**
 * Was malt an dieser Stelle tatsächlich? Der Hit-Test-Stapel wird von oben nach unten
 * durchgegangen: das Logo selbst wird übersprungen, transparente Elemente ebenfalls.
 * Der Header wird bewusst nicht pauschal übersprungen – sobald er einen deckenden
 * Hintergrund hat (Sticky-Zustand, nicht-transparente Seiten), ist genau der der
 * Hintergrund des Logos und nicht das Produktbild dahinter.
 * @returns {{image: HTMLImageElement}|{color: {r: number, g: number, b: number, a: number}}|null}
 */
function backdropAt(x, y) {
  if (x < 0 || y < 0 || x > innerWidth || y > innerHeight) return null;

  for (const element of document.elementsFromPoint(x, y)) {
    if (element.closest(LOGO_SELECTOR)) continue;
    if (element instanceof HTMLImageElement && element.closest(MEDIA_SELECTOR)) {
      return { image: element };
    }
    const color = paintedColor(getComputedStyle(element));
    if (color) return { color };
  }
  return null;
}

/**
 * Deckende Farbe, mit der ein Element malt – oder null, wenn es durchscheinend ist.
 * Der Header-Underlay malt per linear-gradient, hat also eine transparente
 * background-color; ohne die Gradient-Auswertung würde das Produktbild dahinter
 * gemessen und das Logo im Sticky-Zustand weiß auf weißer Leiste stehen.
 * Bitmap-Hintergründe (url(...)) lassen sich so nicht auswerten und gelten als
 * durchscheinend – die Suche läuft dann eine Ebene tiefer weiter.
 */
function paintedColor(style) {
  const solid = parseColor(style.backgroundColor);
  if (solid && solid.a >= OPAQUE_ALPHA) return solid;

  const image = style.backgroundImage;
  if (!image || image === 'none') return null;

  const stops = [...image.matchAll(/\brgba?\([^)]*\)/g)]
    .map((match) => parseColor(match[0]))
    .filter((color) => color && color.a >= OPAQUE_ALPHA);
  if (!stops.length) return null;

  return {
    r: average(stops, 'r'),
    g: average(stops, 'g'),
    b: average(stops, 'b'),
    a: 1,
  };
}

function average(items, key) {
  return items.reduce((sum, item) => sum + item[key], 0) / items.length;
}

/** Mittlere relative Luminanz des Bildausschnitts hinter `rect`, oder null. */
function imageLuminance(bitmap, image, rect) {
  const box = image.getBoundingClientRect();
  // Ohne gerenderte Fläche gibt es kein Verhältnis zwischen Bild- und Bildschirmkoordinaten.
  if (box.width < 1 || box.height < 1 || !bitmap.width || !bitmap.height) return null;

  const style = getComputedStyle(image);
  const scale =
    style.objectFit === 'contain'
      ? Math.min(box.width / bitmap.width, box.height / bitmap.height)
      : Math.max(box.width / bitmap.width, box.height / bitmap.height);

  const [anchorX, anchorY] = parseObjectPosition(style.objectPosition);
  const originX = box.left + (box.width - bitmap.width * scale) * anchorX;
  const originY = box.top + (box.height - bitmap.height * scale) * anchorY;

  const sx = clamp((rect.left - originX) / scale, 0, bitmap.width - 1);
  const sy = clamp((rect.top - originY) / scale, 0, bitmap.height - 1);
  const sw = clamp(rect.width / scale, 1, bitmap.width - sx);
  const sh = clamp(rect.height / scale, 1, bitmap.height - sy);

  const ctx = canvasContext();
  ctx.clearRect(0, 0, SAMPLE_SIZE, SAMPLE_SIZE);
  ctx.drawImage(bitmap, sx, sy, sw, sh, 0, 0, SAMPLE_SIZE, SAMPLE_SIZE);

  let data;
  try {
    ({ data } = ctx.getImageData(0, 0, SAMPLE_SIZE, SAMPLE_SIZE));
  } catch {
    // Verunreinigtes Canvas: Quelle ohne CORS gelesen. Die Verunreinigung ist dauerhaft,
    // deshalb wird das Canvas verworfen, damit spätere Messungen wieder greifen können.
    context = null;
    return null;
  }

  let sum = 0;
  for (let i = 0; i < data.length; i += 4) {
    sum += relativeLuminance(data[i], data[i + 1], data[i + 2]);
  }
  return sum / (data.length / 4);
}

function canvasContext() {
  if (!context) {
    const canvas = document.createElement('canvas');
    canvas.width = SAMPLE_SIZE;
    canvas.height = SAMPLE_SIZE;
    context = canvas.getContext('2d', { willReadFrequently: true });
  }
  return context;
}

/**
 * Lädt das Bild noch einmal per CORS, damit das Canvas auslesbar bleibt – verkleinert,
 * weil für einen Helligkeitsmittelwert keine volle Auflösung nötig ist. Schlägt das fehl
 * (data:/blob:-Quellen, blockiertes CORS), wird direkt aus dem <img> gelesen; ist dieses
 * Canvas dann verunreinigt, wirft getImageData und die Messung entfällt.
 */
function bitmapFor(image) {
  const src = image.currentSrc || image.src;
  if (!src) return Promise.resolve(null);

  let cached = bitmaps.get(src);
  if (!cached) {
    cached = (async () => {
      try {
        const url = new URL(src, location.href);
        if (url.searchParams.has('width')) url.searchParams.set('width', String(SAMPLE_IMAGE_WIDTH));
        const response = await fetch(url.href, { mode: 'cors', credentials: 'omit' });
        if (!response.ok) throw new Error(`${response.status}`);
        return await createImageBitmap(await response.blob());
      } catch {
        if (!image.complete || !image.naturalWidth) return null;
        return createImageBitmap(image);
      }
    })().catch(() => null);
    bitmaps.set(src, cached);
  }
  return cached;
}

function parseColor(value) {
  const match = /^(?:rgba?|color)\(([^)]+)\)/.exec(value);
  if (!match) return null;
  const parts = match[1]
    .replace(/^srgb\s+/, '')
    .split(/[\s,/]+/)
    .filter(Boolean)
    .map(Number);
  if (parts.length < 3 || parts.some(Number.isNaN)) return null;
  return { r: parts[0], g: parts[1], b: parts[2], a: parts.length > 3 ? parts[3] : 1 };
}

/** object-position als Anteil 0–1 je Achse. */
function parseObjectPosition(value) {
  const parts = String(value).trim().split(/\s+/);
  return [anchor(parts[0], 'left', 'right'), anchor(parts[1] ?? parts[0], 'top', 'bottom')];
}

function anchor(part, start, end) {
  if (part === 'center' || part == null) return 0.5;
  if (part === start) return 0;
  if (part === end) return 1;
  if (part.endsWith('%')) return clamp(parseFloat(part) / 100, 0, 1);
  return 0.5;
}

function relativeLuminance(r, g, b) {
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
}

function toLinear(channel) {
  const value = channel / 255;
  return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
}

/** Relative Luminanz -> wahrgenommene Helligkeit (CIE L*, 0–100). */
function lstar(luminance) {
  return luminance <= 216 / 24389 ? (luminance * 24389) / 27 : 116 * Math.cbrt(luminance) - 16;
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}
